// Configuration for the storefront Home page presentation. Persisted as a single
// JSON string on IStorefrontSettings.homePageConfig — the schema-bound backend just
// needs one new field, and it survives buildStorefrontPatch (which only diffs
// primitives and arrays).
//
// IMPORTANT: this is strictly additive. An absent/empty config produces no inline
// styles at all (every builder returns {} for an undefined section), so the Home page
// renders exactly as it does today until a merchant sets a value. The reused style
// interfaces come from searchPageConfig so the two editors share builders/controls.

import { IAutocompleteConfig } from './autocompleteConfig';
import {
    ISearchCardStyle,
    ISearchContainerStyle,
    ISearchElementStyle,
    ISearchPriceStyle,
    ISearchTextStyle,
} from './searchPageConfig';

// One attribute (facet) constraint on a nav pod's filter. Emitted as a facet=Key|Value search
// param. Several may be set: same key OR's, different keys AND — the search page's own semantics.
export interface INavPodAttribute {
    key: string;
    value: string;
}

// A nav pod's filter destination. Any subset may be set; the storefront builds a /search URL from
// whichever fields are present (see navPodSearchUrl in the shopper app's HomePage).
export interface INavPodFilter {
    segment?: string;
    category?: string;
    subCategory?: string;
    manufacturer?: string;
    // Product tags. Emitted as repeated tag= params, which the search page OR's.
    tags?: string[];
    attributes?: INavPodAttribute[];
    // Superseded by `attributes` (which holds any number of constraints). Kept so pods authored
    // before that existed keep working; the storefront folds these into the attribute list.
    attributeKey?: string;
    attributeValue?: string;
}

// A merchant-authored "nav pod": a large, image-backed shortcut that navigates the shopper
// to a filtered /search view or a URL. Rendered larger (and slightly taller than wide) than the
// automatic category pods.
export interface INavPod {
    id: string;
    name?: string;
    // Secondary call-to-action line under the title (e.g. "Shop now ›"). Styled via navPodLink.
    linkText?: string;
    imageUrl?: string;
    // Inline SVG markup for a recolorable icon (CSS-mask tinted by navPodIconColor / hover color).
    // Takes precedence over imageUrl; the SVG's own colors are ignored (only its shape matters).
    iconSvg?: string;
    filter?: INavPodFilter;
    // When set, the pod navigates here instead of the filtered search (takes precedence over
    // `filter`). http(s) URLs open in a new tab; in-store paths (e.g. /page/services) navigate
    // in place.
    linkUrl?: string;
}

export interface IHomePageConfig {
    // The home page hero search bar's suggestion popout (that bar is shown only when the header search
    // is off). Configured independently of the header and search page bars.
    searchAutocomplete?: IAutocompleteConfig;
    // The store title heading above the hero banner (<h1 class="home-title">). No bespoke
    // control — set via the raw Home Page Style JSON.
    pageTitle?: ISearchTextStyle;
    // The hero banner container — the box that wraps the banner image(s) at the top of
    // the home page (background, border, radius, padding, shadow, etc.).
    banner?: ISearchElementStyle;
    // When true the live store renders the banner across the full viewport width while the
    // page content (and the banner's overlay text) stays locked to the page maxWidth.
    bannerFullWidth?: boolean;
    // When true (full-bleed carousels only), the banner is sized to the image's natural height
    // instead of the fixed bannerHeight box — it scales with viewport width and never crops
    // (taller on wide/4K screens). Only safe when banners share one aspect ratio.
    bannerAutoHeight?: boolean;
    // When true (full-bleed only), the banner's overlay text/CTA is sized in container-query units
    // so it scales WITH the banner (grows on wide/4K screens, holds its spot) rather than locking
    // to a fixed pixel maxWidth column.
    bannerOverlayScale?: boolean;
    // Seconds each banner is shown before the carousel advances (absent = 7, the long-standing
    // default). Raise it for a slide with timed artwork — an animated banner running past 7s is
    // otherwise cut off mid-play. 0 turns auto-advance off, leaving the dots to change slide.
    // The live store clamps positive values to a 2s floor; only 2+ banners rotate.
    bannerRotationSeconds?: number;
    // How the banner image fills its box. 'fit' (default when absent) letterboxes via
    // object-fit:contain against the banner's backgroundColor; 'fill' crops via object-fit:cover.
    bannerImageFit?: 'fit' | 'fill';
    // When true the home page's hero banner extends up under a transparent nav header so the bar
    // floats over the full-bleed image. Requires bannerFullWidth; the shopper side applies it on
    // desktop only. Strictly opt-in: unset/false leaves the header solid and above the banner.
    bannerUnderHeader?: boolean;
    // Overlay-bar chrome for the not-yet-scrolled state of bannerUnderHeader mode (once the shopper
    // scrolls, the bar reverts to its normal solid styling). Background is any CSS color — typically
    // rgba with alpha so the header text stays legible over a busy hero without hiding it.
    // Unset = fully transparent bar (today's behavior).
    bannerUnderHeaderBackground?: string;
    // CSS box-shadow for the same not-scrolled overlay state, softening the bar's edge over the
    // hero. Unset = no shadow (today's behavior).
    bannerUnderHeaderShadow?: string;
    // The banner message text shown under the hero banner (.home-message).
    bannerText?: ISearchTextStyle;
    // The floating add-to-cart button on home product cards (mirrors
    // searchPageConfig.addToCartButton). Set via raw JSON.
    addToCartButton?: ISearchElementStyle;
    // Product cards shown inside home sections (mirror the search card controls).
    card?: ISearchCardStyle;
    title?: ISearchTextStyle;
    price?: ISearchPriceStyle;
    // The product image area within each card.
    image?: ISearchElementStyle;
    // Category pods: each pod button, its label, and the row/grid that wraps them.
    pod?: ISearchElementStyle;
    podName?: ISearchTextStyle;
    podContainer?: ISearchContainerStyle;
    // Nav pods: merchant-authored image shortcuts to filtered searches, plus their button,
    // label, and container styling (mirrors the category pod style keys above).
    navPods?: INavPod[];
    // The pod button itself. Its width/height set the pod's dimensions (the editor exposes both).
    navPod?: ISearchElementStyle;
    navPodName?: ISearchTextStyle;
    // The pod's image, and its call-to-action line (INavPod.linkText).
    navPodImage?: ISearchElementStyle;
    navPodLink?: ISearchTextStyle;
    navPodIconColor?: string;                             // tint for nav-pod SVG-mask icons (INavPod.iconSvg)
    navPodIconHoverColor?: string;                        // hover recolor for nav-pod SVG-mask icons
    navPodContainer?: ISearchContainerStyle;
    // When true the pod row is a strict horizontal scroller (nowrap + chevrons, styled by
    // scrollButton/scrollbar) instead of wrapping onto additional lines.
    navPodScroll?: boolean;
    // Horizontal alignment of the pod row. Unset keeps the stylesheet default (centered when
    // wrapping, left when scrolling). 'spaceBetween' spreads the pods across the full width.
    navPodAlign?: 'left' | 'center' | 'right' | 'spaceBetween';
    // Side-scroll sections (IStorefrontSection.scrollProducts): the product scroller's left/right
    // chevron buttons (`scrollButton`, applied to both) and its horizontal scrollbar (`scrollbar`,
    // applied to the scrolling row — use standard scrollbar CSS, e.g.
    // { scrollbarColor: "#ff9900 #000", scrollbarWidth: "thin" }). Set via raw JSON.
    scrollButton?: ISearchElementStyle;
    scrollbar?: ISearchElementStyle;
    moreLikeThisButton?: ISearchElementStyle;            // style the per-section "More like this" link as a button
    // --- Section collapse/expand control (the +/- on a collapsible section's header) ---
    // Page-wide, like every other button style here, and set via raw JSON.
    //
    // `sectionToggle` styles the indicator itself: size, color, background, border, radius, and the
    // `right`/`top` offsets that place it on the header band. Unset keeps the shipped look — a 24px
    // glyph inset 20px from the right at 65% opacity, tinted light or dark automatically to contrast
    // with whatever color the merchant gave the header band.
    //
    // NOTE: setting `color` here opts OUT of that automatic contrast for both header colors, so a
    // store with light AND dark section headers should usually leave color alone.
    sectionToggle?: ISearchElementStyle;
    // Merged over `sectionToggle` while the pointer is anywhere on the section header (the whole band
    // is the toggle, so the indicator reacts to the band's hover, not just its own 24px box).
    //
    // GOTCHA: the indicator is vertically centered with `transform: translateY(-50%)`, so a hover
    // `transform` REPLACES that and the glyph jumps. Include the translate in the hover value —
    // e.g. `translateY(-50%) scale(1.15)` — rather than `scale(1.15)` alone.
    sectionToggleHover?: ISearchElementStyle;
    // The characters drawn when the section is collapsed / expanded. Default '+' and '−' (U+2212).
    // Any string works, so a store can use chevrons ('⌄' / '⌃'), arrows, or its own glyph set.
    // The title TEXT block inside a section's header band (mirrors ac-storefront-web). Page-wide;
    // set `color` on the per-section band, not here, or every section shares one colour.
    sectionTitleText?: ISearchElementStyle;
    sectionToggleExpandIcon?: string;
    sectionToggleCollapseIcon?: string;
    // Hover styling for the home page's interactable tiles, applied as scoped `:hover` rules
    // with a short auto-transition. `cardHover` = product cards; `podHover` = category pods;
    // `navPodHover` = nav pods. Any CSS passes through (boxShadow, transform, borderColor, …).
    cardHover?: ISearchElementStyle;
    podHover?: ISearchElementStyle;
    navPodHover?: ISearchElementStyle;

    // --- Full-bleed "bands" (raw JSON only; mirrors ac-storefront-web homePageConfig) ---
    // Let specific home rows break out of the max-width column to span the full viewport, so a
    // merchant can build edge-to-edge colored bands with the inner content still at maxWidth.
    // Strictly additive: unset = content stays in the column.
    // (Hero banner full-bleed is `bannerFullWidth`, declared with the banner keys above.)
    // Full-width band behind the nav-pod row (e.g. { backgroundColor:'#F58220', padding:'24px 0' });
    // the pods stay centered within maxWidth.
    navPodBand?: ISearchElementStyle;
    // Per-section full-width bands, indexed by section render order (sectionBands[0] = first section).
    // A section with no entry is un-banded. Use to stripe alternating colors (e.g. white / #E7ECF2);
    // drop the section's own border / make it transparent so the band shows through.
    sectionBands?: ISearchElementStyle[];

    // --- Uniform block spacing (opt-in; px; mirrors ac-storefront-web homePageConfig) ---
    // Top padding above the very first home block (title/banner) — closes the header→banner gap.
    homeContentPaddingTop?: number;
    // Uniform vertical gap between every stacked home block (banner, bannerText, nav pods, category
    // pods, search bar, sections). Unset = today's per-block spacing, untouched.
    homeBlockGap?: number;

    // --- Full-width page layout (opt-in; mirrors ac-storefront-web homePageConfig) ---
    // When true, #content-inner spans the full viewport width at all breakpoints instead of
    // the default 90% column, letting full-bleed bands reach the browser edges. Strictly
    // additive: unset/false leaves #content-inner exactly as it is today.
    // Carousel dot placement (mirrors ac-storefront-web). Absent/false = dots overlay the bottom of
    // the hero, as shipped. True = dots sit in normal flow under the banner, which is what this
    // editor's banner preview draws; the storefront only matches once a store opts in.
    bannerDotsBelow?: boolean;
    fullWidthLayout?: boolean;

    // Browse All CTA band (AC-969): the bottom Browse All area rendered as a full-bleed band with an
    // optional heading + subheading above the styled button. `band` carries the background
    // (color/image/backgroundAttachment:'fixed'/minHeight). Absent = plain centered button.
    browseAllBand?: IBrowseAllBand;

    // Affiliations / endorsements / awards band: a row of logo images with an optional label.
    // Strictly opt-in — renders only when items has >=1 entry with an imageUrl.
    affiliations?: IAffiliations;
}

// The bottom "Browse All" area rendered as a call-to-action band instead of the classic centered
// button: a background (solid color, image, or image under a tinted wash) with an optional heading
// and subheading stacked above the button. Named so the editor panel and the canvas preview can
// both speak the type rather than re-deriving it from IHomePageConfig.
export interface IBrowseAllBand {
    heading?: string;
    subheading?: string;
    backgroundColor?: string;
    backgroundImageUrl?: string;
    overlayColor?: string;
    overlayOpacity?: number;
    fixed?: boolean;
    minHeight?: number;
    headingStyle?: ISearchTextStyle;
    subheadingStyle?: ISearchTextStyle;
    buttonStyle?: ISearchElementStyle;
}

// A single affiliation/award/certification/endorsement logo.
export interface IAffiliationItem {
    id: string;
    imageUrl?: string;
    altText?: string;
    linkUrl?: string;   // optional; when set the logo links here, opening in a new tab
}

// Affiliations / endorsements / awards band. Optional — renders only when items has >=1 entry
// with an imageUrl. Lives inside homePageConfig (no BFF/API change).
export interface IAffiliations {
    items?: IAffiliationItem[];
    label?: string;
    description?: string;                                 // optional blurb rendered under the label (title)
    descriptionStyle?: ISearchTextStyle;
    labelPosition?: 'top' | 'left' | 'bottom' | 'none';   // label placement relative to the logo row
    position?: 'top' | 'bottom';                          // home-page placement (default 'bottom')
    align?: 'left' | 'center' | 'right' | 'spaceBetween'; // logo row alignment (default 'center')
    gap?: number;                                         // px gap between logos (default 24)
    scroll?: boolean;                                     // auto-scrolling marquee instead of a static row (pauses on hover)
    scrollSpeed?: number;                                 // seconds for one full loop (default 30; lower = faster)
    band?: ISearchElementStyle;                           // full-bleed band background
    pod?: ISearchElementStyle;                            // each logo pod
    image?: ISearchElementStyle;                          // the logo <img>
    labelStyle?: ISearchTextStyle;                        // label text style
}

// The flex justify-content for a nav pod alignment. Undefined leaves the stylesheet's default in
// place (centered when wrapping, left-aligned when scrolling), so an unset config changes nothing.
export function navPodJustify(align?: IHomePageConfig['navPodAlign']): string | undefined {
    switch (align) {
        case 'left': return 'flex-start';
        case 'center': return 'center';
        case 'right': return 'flex-end';
        case 'spaceBetween': return 'space-between';
        default: return undefined;
    }
}

// The same alignment for the SCROLLING pod row (navPodScroll), where a plain `center` / `flex-end` is a
// trap: once the pods overflow, centering splits the overflow to BOTH sides and the left half lands in
// negative scroll space the shopper can never reach, so the first pod is permanently clipped. `safe`
// falls back to start alignment exactly when the content overflows. Mirrors the storefront runtime
// (ac-storefront-web Components/Helpers/homePageConfig.ts) so this preview matches the live store.
export function navPodScrollJustify(align?: IHomePageConfig['navPodAlign']): string | undefined {
    const justify = navPodJustify(align);
    return justify === 'center' || justify === 'flex-end' ? `safe ${justify}` : justify;
}

// The carousel's dwell time per slide, mirrored from ac-storefront-web homePageConfig.ts so the
// editor preview rotates at the pace the shopper will see. Unset (or unusable — NaN, Infinity, a
// string from hand-edited JSON) resolves to the 7s the storefront always hardcoded; 0 or negative
// disables auto-advance (null); positive values hold to a 2s floor so a typo can't thrash.
export const BANNER_ROTATION_DEFAULT_SECONDS = 7;
export const BANNER_ROTATION_MIN_SECONDS = 2;
export function bannerRotationMs(config?: Pick<IHomePageConfig, 'bannerRotationSeconds'>): number | null {
    const seconds = config?.bannerRotationSeconds;
    if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return BANNER_ROTATION_DEFAULT_SECONDS * 1000;
    if (seconds <= 0) return null;
    return Math.max(seconds, BANNER_ROTATION_MIN_SECONDS) * 1000;
}

export const DEFAULT_HOME_PAGE_CONFIG: IHomePageConfig = {};

export function parseHomePageConfig(raw?: string): IHomePageConfig {
    if (!raw) return { ...DEFAULT_HOME_PAGE_CONFIG };
    try {
        const parsed = JSON.parse(raw) as IHomePageConfig;
        return { ...DEFAULT_HOME_PAGE_CONFIG, ...parsed };
    } catch {
        return { ...DEFAULT_HOME_PAGE_CONFIG };
    }
}

export function serializeHomePageConfig(config: IHomePageConfig): string {
    return JSON.stringify(config);
}
